<template>
  <div
    class="fixed left-0 top-0 h-full border-r border-gray-100 bg-white transition-all duration-500 ease-in-out z-50"
    :class="[isCollapsed ? 'w-20' : 'w-64']"
    @mouseenter="handleMouseEnter"
    @mouseleave="handleMouseLeave"
  >
    <!-- Logo Area -->
    <div
      class="flex items-center justify-between h-20 px-4 border-b border-gray-100"
    >
      <div class="flex items-center space-x-3 overflow-hidden">
        <div class="flex-shrink-0">
          <div
            class="w-10 h-10 rounded-xl flex items-center justify-center shadow-lg"
          >
            <img :src="icon" />
          </div>
        </div>
        <div
          class="transition-all duration-500"
          :class="[isCollapsed ? 'opacity-0 w-0' : 'opacity-100 w-auto']"
        >
          <h2 class="text-lg font-bold text-gray-800 whitespace-nowrap">
            Nobel Lending
          </h2>
          <p class="text-xs text-gray-500 whitespace-nowrap">
            Verification System
          </p>
        </div>
      </div>

      <!-- Collapse Button -->
      <!-- <button
        @click="toggleSidebar"
        class="p-2 rounded-lg hover:bg-gray-100 transition-all duration-300 hover:scale-110"
        :class="{ 'rotate-180': isCollapsed }"
      >
        <svg
          class="w-5 h-5 text-gray-500"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M11 19l-7-7 7-7m8 14l-7-7 7-7"
          />
        </svg>
      </button> -->
    </div>

    <!-- User Profile Section -->
    <div class="p-4 border-b border-gray-100">
      <div class="flex items-center space-x-3">
        <div class="relative flex-shrink-0">
          <div
            class="w-8 h-8 mr-1 bg-white/20 rounded-xl flex items-center justify-center"
          >
            <span class="rounded-xl shadow-lg p-2 text-white bg-primary-50">
              <svg
                class="w-8 h-8"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                />
              </svg>
            </span>
          </div>
        </div>
        <div
          class="transition-all duration-500 overflow-hidden"
          :class="[isCollapsed ? 'opacity-0 w-0' : 'opacity-100 w-auto']"
        >
          <p class="text-sm font-semibold text-gray-800 whitespace-nowrap">
            {{ username || "N/A" }}
          </p>
          <p class="text-xs text-gray-500 whitespace-nowrap">
            {{ role }}
          </p>
        </div>
      </div>
    </div>

    <!-- Navigation Menu -->
    <div class="py-6 overflow-y-auto h-[calc(100vh-200px)] custom-scrollbar">
      <div
        v-for="(section, sectionIndex) in menuSections"
        :key="sectionIndex"
        class="mb-6"
        v-show="section.role ? role == section.role : true"
      >
        <!-- Section Title (hidden when collapsed) -->
        <div
          class="px-4 mb-2 transition-all duration-500"
          :class="[isCollapsed ? 'opacity-0 h-0' : 'opacity-100 h-auto']"
        >
          <p
            class="text-xs font-semibold text-gray-400 uppercase tracking-wider"
          >
            {{ section.title }}
          </p>
        </div>

        <!-- Menu Items -->
        <div v-for="(item, index) in section.items" :key="index">
          <router-link
            :to="item.path"
            custom
            v-slot="{ navigate, href, isActive }"
          >
            <a
              :href="href"
              @click="navigate"
              class="relative flex items-center mx-2 px-4 py-3 rounded-xl transition-all duration-300 group"
              :class="[
                isActive
                  ? 'bg-primary-50 text-white shadow-lg'
                  : 'text-gray-600 hover:bg-gray-100',
              ]"
            >
              <!-- Icon -->
              <span class="flex-shrink-0" v-html="item.icon"></span>

              <!-- Label (hidden when collapsed) -->
              <span
                class="ml-3 text-sm font-medium transition-all duration-500 whitespace-nowrap"
                :class="[
                  isCollapsed
                    ? 'opacity-0 w-0 ml-0'
                    : 'opacity-100 w-auto ml-3',
                ]"
              >
                {{ item.label }}
              </span>

              <!-- Badge (if any) -->
              <span
                v-if="item.badge"
                class="ml-auto px-2 py-1 text-xs rounded-full transition-all duration-500"
                :class="[
                  isActive
                    ? 'bg-white text-indigo-800'
                    : 'bg-red-100 text-red-600',
                  isCollapsed ? 'opacity-0 scale-0' : 'opacity-100 scale-100',
                ]"
              >
                {{ item.badge }}
              </span>

              <!-- Tooltip when collapsed -->
              <div
                v-if="isCollapsed"
                class="absolute left-full ml-2 px-3 py-2 bg-gray-900 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap z-50 pointer-events-none"
              >
                {{ item.label }}
                <span
                  v-if="item.badge"
                  class="ml-2 px-1.5 py-0.5 bg-red-500 text-xs rounded-full"
                >
                  {{ item.badge }}
                </span>
              </div>
            </a>
          </router-link>
        </div>
      </div>
    </div>

    <!-- Bottom Actions -->
    <div
      class="absolute bottom-0 left-0 right-0 p-4 bg-white border-t border-gray-100"
    >
      <button
        @click="logout"
        class="flex border border-orange-600 items-center w-full px-4 py-3 text-orange-600 hover:bg-gray-100 rounded-xl transition-all duration-300 group"
      >
        <svg
          class="w-5 h-5 flex-shrink-0"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
          />
        </svg>
        <span
          class="ml-3 text-sm font-medium transition-all duration-500 whitespace-nowrap"
          :class="[
            isCollapsed ? 'opacity-0 w-0 ml-0' : 'opacity-100 w-auto ml-3',
          ]"
        >
          Logout
        </span>
        <!-- Tooltip when collapsed -->
        <div
          v-if="isCollapsed"
          class="absolute left-full ml-2 px-3 py-2 bg-gray-900 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap z-50 pointer-events-none"
        >
          Logout
        </div>
      </button>
    </div>
  </div>

  <!-- Main Content Overlay when collapsed on mobile -->
  <div
    v-if="isMobileOpen"
    class="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden transition-opacity duration-300"
    @click="closeMobileSidebar"
  ></div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from "vue";
import { useRouter } from "vue-router";

import { useAuthStore } from "@/stores/authStore";

const authStore = useAuthStore();

import icon from "@/assets/icon.png";

const router = useRouter();
const isCollapsed = ref(false);
const isMobileOpen = ref(false);
const isHovered = ref(false);
// const isSelectedMenu = ref("Dashboard");

// State
const username = ref("N/A");
const role = ref("N/A");

// Menu sections with icons (SVG strings)
const menuSections = [
  {
    title: "MAIN",
    items: [
      {
        label: "Dashboard",
        path: "/admin",
        icon: `<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
              </svg>`,
      },
      {
        label: "Tickets",
        path: "/admin/tickets",
        icon: `<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
              </svg>`,
        // badge: "0",
      },
    ],
  },
  {
    title: "MANAGEMENT",
    items: [
      {
        label: "My Tickets",
        path: "/admin/my-tickets",
        icon: `<svg
          class="w-5 h-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
          />
        </svg>`,
      },
      {
        label: "Call Attempts",
        path: "/admin/call-attempts",
        icon: `<svg
          class="w-5 h-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
          />
        </svg>`,
        // badge: "0",
      },
      // {
      //   label: "Calendar",
      //   path: "/admin/calendar",
      //   icon: `<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      //           <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
      //         </svg>`,
      // },
      // {
      //   label: "Reports",
      //   path: "/admin/reports",
      //   icon: `<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      //           <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
      //         </svg>`,
      // },
    ],
  },
  {
    title: "ADMIN",
    role: "Supervisor",
    items: [
      {
        label: "Users",
        path: "/admin/users",
        icon: `<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>`,
      },
      {
        label: "Clients",
        path: "/admin/clients",
        icon: `<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>`,
      },
      {
        label: "Activity Logs",
        path: "/admin/activity-logs",
        icon: `<svg
          class="w-5 h-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
          />
        </svg>`,
      },
      // {
      //   label: "Activity Logs",
      //   path: "/admin/activity-logs",
      //   icon: `<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      //               <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
      //             </svg>`,
      // },
      // {
      //   label: "Settings",
      //   path: "/admin/settings",
      //   icon: `<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      //           <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
      //           <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      //         </svg>`,
      // },
    ],
  },
];

if (authStore.checkAuth()) {
  const user = authStore.user;
  username.value = user.name;
  role.value = user.role;
} else {
  // Log out user
}

// Handle mouse enter/leave for auto-expand (optional)
const handleMouseEnter = () => {
  isHovered.value = true;
};

const handleMouseLeave = () => {
  isHovered.value = false;
  if (isCollapsed.value) {
    // Keep collapsed state
  }
};

// Toggle sidebar
// const toggleSidebar = () => {
//   isCollapsed.value = !isCollapsed.value;
//   localStorage.setItem("sidebarCollapsed", String(isCollapsed.value));
// };

// Logout function
const logout = () => {
  localStorage.removeItem("token");
  router.push("/login");
};

// Mobile menu handlers
// const openMobileSidebar = () => {
//   isMobileOpen.value = true;
// };

const closeMobileSidebar = () => {
  isMobileOpen.value = false;
};

// Load saved state
onMounted(() => {
  const saved = localStorage.getItem("sidebarCollapsed");
  if (saved !== null) {
    isCollapsed.value = saved === "true";
  }

  // Handle responsive behavior
  const handleResize = () => {
    if (window.innerWidth < 1024) {
      isCollapsed.value = true;
    }
  };

  window.addEventListener("resize", handleResize);
  handleResize();

  onUnmounted(() => {
    window.removeEventListener("resize", handleResize);
  });
});
</script>

<style scoped>
/* Custom Scrollbar */
.custom-scrollbar::-webkit-scrollbar {
  width: 4px;
}

.custom-scrollbar::-webkit-scrollbar-track {
  background: transparent;
}

.custom-scrollbar::-webkit-scrollbar-thumb {
  background: #cbd5e0;
  border-radius: 20px;
}

.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

/* Transitions */
.slide-enter-active,
.slide-leave-active {
  transition: transform 0.3s ease;
}

.slide-enter-from,
.slide-leave-to {
  transform: translateX(-100%);
}

/* Hover effects */
.hover-scale {
  transition: transform 0.2s ease;
}

.hover-scale:hover {
  transform: scale(1.05);
}

/* Active link indicator */
.router-link-active .active-indicator {
  opacity: 1;
}
</style>
